import React, { useMemo } from "react";
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Box, Stack, Chip, Typography, Link as MuiLink, Paper, Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Link as RouterLink } from "react-router-dom";
import PanelHeading from "./PanelHeading";
import SeatBar from "./SeatBar";
import ResponsiveTable from "../../components/ResponsiveTable";
import { statusStyleFor } from "./dashboardChips";

const MAX_ROWS = 6;

const HINT = "Each group with its lead tutor, seats taken and week progress. "
  + "Current state — the date range above does not filter this panel.";

/**
 * Course delivery readiness.
 *
 * Neither endpoint answers this alone: `admin/dashboard/course-plans` carries the course name and
 * week progress but no staffing, and `admin/analytics/batch-capacity` carries the lead tutor and
 * seat counts but no week. Rows are driven by the plans list — it is already ordered
 * active → upcoming → completed and limited server-side — and enriched from the capacity report.
 */
const noLeadChip = (
  <Chip
    size="small"
    icon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
    label="No lead tutor"
    sx={{
      bgcolor: "#fef3c7",
      color: "#92400e",
      fontWeight: 700,
      "& .MuiChip-icon": { color: "#92400e", ml: 0.5 },
    }}
  />
);

/**
 * Seats for one row. The capacity report is preferred because it comes straight from
 * CoursePlanSeatMapper; the plans payload is the fallback and exposes the limit only indirectly,
 * as enrolled + available.
 */
function seatsOf(plan, cap) {
  if (cap) {
    return { filled: cap.filled_seats ?? 0, capacity: cap.capacity ?? null };
  }
  const enrolled = plan.enrolled_count ?? 0;
  // The group's own capacity (null = no limit). Rebuilding it from enrolled + available
  // turned the course-wide seat pool into this group's capacity.
  if (plan.capacity !== undefined) {
    return { filled: enrolled, capacity: plan.capacity };
  }
  const available = plan.available_seats;
  return {
    filled: enrolled,
    capacity: available == null ? null : enrolled + available,
  };
}

export default function CourseDeliveryPanel({ plans, capacity }) {
  const rows = useMemo(() => {
    const byPlanId = new Map(
      (capacity || []).map((c) => [Number(c.course_plan_id), c])
    );
    return (plans || []).slice(0, MAX_ROWS).map((p) => {
      const cap = byPlanId.get(Number(p.id));
      const tutors = cap?.assigned_tutors || [];
      return {
        id: p.id,
        groupName: p.plan_name || p.name || "—",
        courseName: p.course_name,
        status: p.status,
        currentWeek: p.current_week,
        totalWeeks: p.total_weeks,
        leadTutor: tutors[0] || null,
        extraTutors: Math.max(0, tutors.length - 1),
        helperCount: cap?.helper_count ?? 0,
        // Who teaches what here. Empty when the group is not split by subject, in which case the
        // lead covers everything and the tutor cell stays as it was.
        subjectStaff: (cap?.subject_staff || []).filter((s) => s.staff.length > 0),
        uncoveredSubjects: cap?.uncovered_subjects || [],
        location: cap?.location || null,
        ...seatsOf(p, cap),
      };
    });
  }, [plans, capacity]);

  const tutorCell = (r) => {
    if (!r.leadTutor) return noLeadChip;
    const extras = [
      r.extraTutors ? `+${r.extraTutors} lead` : null,
      r.helperCount ? `+${r.helperCount} assistant${r.helperCount === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    return (
      <Box>
        <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }} noWrap>
          {r.leadTutor}
        </Typography>
        {extras.length > 0 && (
          <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap>
            {extras.join(" · ")}
          </Typography>
        )}
        {/* Where the group is split, "the lead tutor" no longer answers who teaches Maths — so
            name the subject tutors rather than making an admin open the Staff tab to find out. */}
        {r.subjectStaff.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {r.subjectStaff.map((s) => (
              <Tooltip
                key={s.subject_id}
                title={`${s.subject_name}: ${s.staff.map((m) => m.name).join(", ")}`}
              >
                <Chip
                  size="small"
                  label={`${s.subject_name} · ${s.staff[0].name}`}
                  sx={{
                    height: 20,
                    maxWidth: 190,
                    bgcolor: "#eef2ff",
                    color: "#4338ca",
                    fontWeight: 600,
                    "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        )}
        {r.uncoveredSubjects.length > 0 && (
          <Typography variant="caption" sx={{ color: "#9a3412", fontWeight: 700 }} display="block">
            No tutor: {r.uncoveredSubjects.join(", ")}
          </Typography>
        )}
      </Box>
    );
  };

  const groupCell = (r) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }} noWrap>
        {r.groupName}
      </Typography>
      <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap display="block">
        {[r.courseName, r.location].filter(Boolean).join(" · ") || "—"}
      </Typography>
    </Box>
  );

  const weekLabel = (r) =>
    r.currentWeek ? `${r.currentWeek} / ${r.totalWeeks || "?"}` : "—";

  const statusCell = (r) => {
    const st = statusStyleFor(r.status);
    return <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600 }} />;
  };

  const head = (label, props = {}) => (
    <TableCell sx={{ fontWeight: 800, color: "#111827" }} {...props}>{label}</TableCell>
  );

  const cardView = (
    <Stack spacing={1}>
      {rows.map((r) => (
        <Box key={r.id} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            {groupCell(r)}
            {statusCell(r)}
          </Stack>
          <Box mt={1}>{tutorCell(r)}</Box>
          <Stack direction="row" alignItems="center" spacing={2} mt={1}>
            <SeatBar filled={r.filled} capacity={r.capacity} compact />
            <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600 }}>
              Week {weekLabel(r)}
            </Typography>
          </Stack>
        </Box>
      ))}
      {rows.length === 0 && (
        <Typography variant="body2" sx={{ color: "#374151" }}>No groups yet</Typography>
      )}
    </Stack>
  );

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        border: "1px solid #eef0f4",
      }}
    >
      <PanelHeading
        title="Course Delivery"
        hint={HINT}
        action={(
          <MuiLink
            component={RouterLink}
            to="/admin/analytics/batch-capacity"
            underline="hover"
            sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
          >
            View all
          </MuiLink>
        )}
      />
      <ResponsiveTable cardView={cardView} elevation={0} sx={{ boxShadow: "none" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {head("Course · Group")}
              {head("Lead tutor")}
              {head("Seats")}
              {head("Week", { align: "right" })}
              {head("Status")}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{groupCell(r)}</TableCell>
                <TableCell>{tutorCell(r)}</TableCell>
                <TableCell><SeatBar filled={r.filled} capacity={r.capacity} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "#111827" }}>
                  {weekLabel(r)}
                </TableCell>
                <TableCell>{statusCell(r)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">No groups yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </Paper>
  );
}
